<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { setEventAccess, updateCommunity } from '../../lib/convexApi';
  import type {
    CommunityDetail,
    CommunityEventSummary,
    EventApplicationMode,
    EventTicketAccess,
    EventVisibilityOverride
  } from '../../lib/types';
  import QuestionnaireBuilder from './QuestionnaireBuilder.svelte';
  import Notice from '../ui/Notice.svelte';
  import { DEFAULT_WELCOME_MESSAGE } from '../../lib/whatsapp';

  export let community: CommunityDetail;

  const dispatch = createEventDispatcher<{ changed: void }>();

  let name = community.name;
  let description = community.description ?? '';
  let visibility = community.visibility;
  let applicationMode = community.applicationMode ?? 'note';
  let allowInvites = community.defaultAllowInvites ?? false;
  let inviteAllowance = community.defaultInviteAllowance ?? null;
  let themeColor: string | null = community.themeColor ?? null;
  let logoUrl = community.logoUrl ?? '';
  let welcomeMessage = community.welcomeMessage ?? '';

  let saving = false;
  let saveError: string | null = null;
  let saveSuccess = false;

  let eventPendingId: string | null = null;
  let eventError: string | null = null;

  async function handleSave() {
    saving = true;
    saveError = null;
    saveSuccess = false;
    try {
      await updateCommunity({
        communityId: community.id,
        name: name.trim() || undefined,
        description: description.trim() || null,
        visibility,
        applicationMode,
        defaultAllowInvites: allowInvites,
        defaultInviteAllowance: inviteAllowance,
        themeColor,
        logoUrl: logoUrl.trim() || null,
        welcomeMessage: welcomeMessage.trim() || null
      });
      saveSuccess = true;
      dispatch('changed');
    } catch (err) {
      saveError = err instanceof Error ? err.message : 'Could not save settings.';
    } finally {
      saving = false;
    }
  }

  async function handleEventAccessChange(
    event: CommunityEventSummary,
    field: 'visibilityOverride' | 'ticketAccess' | 'applicationMode',
    value: string
  ) {
    eventPendingId = event.id;
    eventError = null;
    try {
      await setEventAccess({
        eventId: event.id,
        [field]: value
      } as {
        eventId: string;
        visibilityOverride?: EventVisibilityOverride;
        ticketAccess?: EventTicketAccess;
        applicationMode?: EventApplicationMode;
      });
      dispatch('changed');
    } catch (err) {
      eventError = err instanceof Error ? err.message : 'Could not update event access.';
    } finally {
      eventPendingId = null;
    }
  }

  function onSelectChange(
    event: CommunityEventSummary,
    field: 'visibilityOverride' | 'ticketAccess' | 'applicationMode'
  ) {
    return (domEvent: Event) => {
      const value = (domEvent.target as HTMLSelectElement).value;
      void handleEventAccessChange(event, field, value);
    };
  }

  function onAllowanceInput(domEvent: Event) {
    const raw = (domEvent.target as HTMLInputElement).value;
    inviteAllowance = raw === '' ? null : Number(raw);
  }

  function onColorInput(domEvent: Event) {
    themeColor = (domEvent.target as HTMLInputElement).value;
  }
</script>

<section class="settings surface surface--sunken" aria-labelledby="settings-heading">
  <h3 id="settings-heading">Community settings</h3>

  {#if saveError}
    <Notice variant="error">{saveError}</Notice>
  {:else if saveSuccess}
    <Notice variant="success">Settings saved.</Notice>
  {/if}

  <form class="settings__form" on:submit|preventDefault={handleSave}>
    <label class="settings__field">
      <span>Name</span>
      <input type="text" bind:value={name} maxlength="100" />
    </label>

    <label class="settings__field">
      <span>Description</span>
      <textarea rows="3" bind:value={description} maxlength="2000"></textarea>
    </label>

    <label class="settings__field">
      <span>Visibility</span>
      <select bind:value={visibility}>
        <option value="public">Public — anyone can join instantly</option>
        <option value="apply">Apply — listed, joining needs approval</option>
        <option value="hidden">Hidden — link-only, joining needs approval</option>
      </select>
    </label>

    <label class="settings__field">
      <span>Welcome message (after accepting)</span>
      <textarea rows="4" bind:value={welcomeMessage} maxlength="1000" placeholder={DEFAULT_WELCOME_MESSAGE}></textarea>
      <small>Prefilled into the WhatsApp “say hi” link on the Members tab. Placeholders: {'{first}'} {'{name}'} {'{community}'} {'{url}'}. Empty = the default above.</small>
    </label>

    <label class="settings__field">
      <span>Application form</span>
      <select bind:value={applicationMode}>
        <option value="note">Simple — optional message</option>
        <option value="questionnaire">Questionnaire — applicants answer your questions</option>
      </select>
    </label>

    <div class="settings__identity">
      <label class="settings__field settings__field--narrow" for="settings-accent">
        <span>Accent color</span>
        <div class="settings__color">
          <input
            id="settings-accent"
            type="color"
            value={themeColor ?? '#ff4d17'}
            on:input={onColorInput}
          />
          <code>{themeColor ?? 'Gravity default'}</code>
          {#if themeColor}
            <button type="button" class="ghost settings__color-reset" on:click={() => (themeColor = null)}>
              Reset
            </button>
          {/if}
        </div>
      </label>
      <label class="settings__field">
        <span>Logo URL</span>
        <input type="url" bind:value={logoUrl} placeholder="https://… (an image you host yourself)" maxlength="500" />
      </label>
      <p class="settings__hint">
        The accent color re-tints your community and ticket pages. The logo shows on your community page —
        link an image you host (we don't store images).
      </p>
    </div>

    <div class="settings__invites">
      <label class="settings__checkbox">
        <input type="checkbox" bind:checked={allowInvites} />
        Allow invites by default for this community's events
      </label>
      <label class="settings__field settings__field--narrow">
        <span>Default plus-ones per guest</span>
        <input
          type="number"
          min="1"
          placeholder="1"
          value={inviteAllowance ?? ''}
          on:input={onAllowanceInput}
        />
      </label>
    </div>

    <button type="submit" class="accent settings__save" disabled={saving}>
      {saving ? 'Saving…' : 'Save settings'}
    </button>
  </form>

  {#if applicationMode === 'questionnaire'}
    <div class="settings__section">
      <h4>Application questionnaire</h4>
      <p class="settings__hint">
        Applicants answer these questions when they apply
        {community.visibility === 'public' ? '(only used by apply-gated events)' : ''}.
      </p>
      <QuestionnaireBuilder communityId={community.id} on:saved={() => dispatch('changed')} />
    </div>
  {/if}

  {#if community.events.length > 0}
    <div class="settings__section">
      <h4>Event access</h4>
      <p class="settings__hint">
        Defaults follow the community. Override per event who can see it and how people get tickets.
        Ticket tiers, prices and capacity live in the Events tab.
      </p>
      {#if eventError}
        <Notice variant="error">{eventError}</Notice>
      {/if}
      <ul class="settings__events">
        {#each community.events as event (event.id)}
          <li class="settings__event" class:settings__event--pending={eventPendingId === event.id}>
            <span class="settings__event-name">{event.name}</span>
            <div class="settings__event-controls">
              <label>
                Visibility
                <select
                  value={event.overrides?.visibilityOverride ?? 'inherit'}
                  on:change={onSelectChange(event, 'visibilityOverride')}
                  disabled={eventPendingId === event.id}
                >
                  <option value="inherit">Inherit from community</option>
                  <option value="public">Public — always listed</option>
                  <option value="unlisted">Unlisted — link only</option>
                </select>
              </label>
              <label>
                Tickets
                <select
                  value={event.overrides?.ticketAccess ?? 'invite'}
                  on:change={onSelectChange(event, 'ticketAccess')}
                  disabled={eventPendingId === event.id}
                >
                  <option value="invite">Invite only (default)</option>
                  <option value="members">Members can claim</option>
                  <option value="open">Anyone can claim</option>
                </select>
              </label>
              <label>
                Application form
                <select
                  value={event.overrides?.applicationMode ?? 'inherit'}
                  on:change={onSelectChange(event, 'applicationMode')}
                  disabled={eventPendingId === event.id}
                >
                  <option value="inherit">Community default</option>
                  <option value="note">Simple message</option>
                  <option value="questionnaire">Questionnaire</option>
                </select>
              </label>
            </div>
          </li>
        {/each}
      </ul>
    </div>
  {/if}
</section>

<style>
  .settings {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-5);
    border-radius: var(--radius-lg);
  }

  .settings h3 {
    margin: 0;
    font-size: var(--font-size-md);
  }

  .settings h4 {
    margin: 0 0 var(--space-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .settings__hint {
    margin: 0 0 var(--space-3);
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
  }

  .settings__form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    max-width: 480px;
  }

  .settings__field {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }

  .settings__field--narrow {
    max-width: 220px;
  }

  .settings__field input,
  .settings__field textarea,
  .settings__field select {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
  }

  .settings__field textarea {
    resize: vertical;
  }

  .settings__invites {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .settings__identity {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .settings__color {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .settings__color input[type='color'] {
    width: 44px;
    height: 32px;
    padding: 2px;
    border: var(--border-subtle);
    background: var(--color-surface-900);
    cursor: pointer;
  }

  .settings__color code {
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
  }

  .settings__color-reset {
    padding: var(--space-1) var(--space-3);
    font-size: var(--font-size-xs);
  }

  .settings__checkbox {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
  }

  .settings__save {
    align-self: flex-start;
  }

  .settings__section {
    border-top: var(--border-subtle);
    padding-top: var(--space-4);
  }

  .settings__events {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .settings__event {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-850);
  }

  .settings__event--pending {
    opacity: 0.7;
  }

  .settings__event-name {
    font-weight: 600;
    font-size: var(--font-size-sm);
  }

  .settings__event-controls {
    display: flex;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .settings__event-controls label {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    flex: 1;
    min-width: 160px;
  }

  .settings__event-controls select {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
  }

</style>
